import { cn } from '@/nucleo/utilitarios';
import { formatarHsl, formatarRazao } from '@/ui/estilos/contraste';
import { paraRgb255, type Hsl } from '@/ui/estilos/lerTokens';

/** Um par da auditoria em que o token entra, já medido no tema em exibição. */
export interface ParMedido {
  /** Texto do par, como o auditor o descreve. */
  descricao: string;
  /** O outro token do par. */
  com: string;
  /** Se este token é a frente (texto/traço) ou o fundo do par. */
  papel: 'frente' | 'fundo';
  razao: number;
  minimo: number;
}

interface ColorSwatchProps {
  nome: string;
  token: string;
  /** Valor lido de tokens.css no tema em exibição. Obrigatório: não há cor padrão. */
  hsl: Hsl;
  uso?: string;
  pares?: ParMedido[];
  className?: string;
}

function nivel(par: ParMedido): { rotulo: string; className: string } {
  if (par.razao < par.minimo) {
    return {
      rotulo: 'Abaixo do mínimo',
      className: 'bg-card text-destructive-text border-destructive',
    };
  }
  // Texto normal: AA a partir de 4,5:1 e AAA a partir de 7:1. Elemento de
  // interface (foco, série de gráfico) tem um nível só na WCAG 2.1: 3:1.
  if (par.minimo >= 4.5 && par.razao >= 7) {
    return { rotulo: 'AAA', className: 'bg-card text-foreground border-success' };
  }
  return { rotulo: 'AA', className: 'bg-card text-foreground border-success' };
}

function paraHex(cor: Hsl): string {
  return `#${paraRgb255(cor)
    .map((canal) => canal.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/**
 * Amostra de um token: a cor é pintada pelo valor lido de `tokens.css` (não
 * por `var(--x)`), para a amostra de cada tema mostrar aquele tema mesmo com
 * a página em outro. As razões vêm de `ui/estilos/contraste.ts`, a mesma conta
 * que o build confere.
 */
export function ColorSwatch({ nome, token, hsl, uso, pares, className }: ColorSwatchProps) {
  const valor = formatarHsl(hsl);

  return (
    <div
      className={cn('ds-swatch rounded-xl border border-border bg-card overflow-hidden', className)}
      data-search-id={`color-${token}`}
      data-token={token}
    >
      <div
        className="h-20 w-full border-b border-border"
        data-print-keep-bg=""
        style={{ backgroundColor: `hsl(${valor})` }}
      />
      <div className="p-3 space-y-1.5">
        <p className="text-sm font-semibold text-foreground">{nome}</p>
        <div className="space-y-0.5">
          <p className="text-[11px] font-mono text-muted-foreground">--{token}</p>
          <p className="text-[11px] font-mono text-muted-foreground" data-valor-hsl="">
            {valor}
          </p>
          <p className="text-[11px] font-mono text-muted-foreground">{paraHex(hsl)}</p>
        </div>
        {uso && <p className="text-[11px] text-muted-foreground leading-snug">{uso}</p>}
        {pares && pares.length > 0 && (
          <ul className="space-y-1 pt-1">
            {pares.map((par) => {
              const n = nivel(par);
              return (
                <li key={`${par.descricao}-${par.com}`} className="space-y-0.5">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                      n.className,
                    )}
                  >
                    {n.rotulo} · {formatarRazao(par.razao)}
                  </span>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    {par.papel === 'frente' ? `sobre --${par.com}` : `--${par.com} por cima`} —{' '}
                    {par.descricao} (mínimo {formatarRazao(par.minimo)})
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
